import React from 'react';
import { 
  Box, 
  IconButton, 
  Typography, 
  List, 
  ListItem, 
  ListItemText,
  ListItemSecondaryAction,
  Button,
  Divider,
  Tooltip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { SideDrawer } from '../../styles/StyledComponents';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LogoutIcon from '@mui/icons-material/Logout';
import EditIcon from '@mui/icons-material/Edit';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import RoleSelector from '../roles/RoleSelector';

const Sidebar = ({
  open,
  onClose,
  selectedRole,
  onRoleSelect,
  onNewChat,
  chatHistory,
  selectedChat,
  onLoadChat,
  onDeleteChat,
  onEditTitle,
  editingTitle,
  editTitleValue,
  onSaveTitle,
  onCancelEdit,
  onLogout,
}) => {
  const navigate = useNavigate();

  return (
    <SideDrawer
      sx={{
        width: 240,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: 240,
          boxSizing: 'border-box',
          backgroundColor: 'background.paper',
          borderRight: 1,
          borderColor: 'divider'
        },
      }}
      variant="persistent"
      anchor="left"
      open={open}
    >
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%'
      }}>
        {/* Header */}
        <Box sx={{ 
          p: 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <Typography variant="h6" sx={{ color: '#F9FAFB' }}>
            AI Chat
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Logout">
              <IconButton 
                onClick={onLogout} 
                size="small"
                sx={{ 
                  color: '#F9FAFB',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose} sx={{ color: '#F9FAFB' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* New Chat Button */}
        <Button
          startIcon={<AddIcon />}
          onClick={onNewChat}
          sx={{
            color: '#F9FAFB',
            justifyContent: 'flex-start',
            pl: 3,
            py: 1.5,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          New Chat
        </Button>

        {/* Role Selector */}
        <RoleSelector
          selectedRole={selectedRole}
          onRoleSelect={onRoleSelect}
        />

        <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

        {/* Navigation Links */}
        <List>
          <ListItem 
            button 
            onClick={() => navigate('/stock-premarket')}
            sx={{
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <ShowChartIcon sx={{ mr: 2 }} />
            <ListItemText primary="Stock Premarket Screener" />
          </ListItem>
        </List>

        <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

        {/* Chat History */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <Typography
            variant="subtitle2"
            sx={{
              px: 2,
              py: 1,
              color: 'rgba(255, 255, 255, 0.7)',
              fontWeight: 500
            }}
          >
            Chat History
          </Typography>
          <List sx={{ px: 1 }}>
            {chatHistory.map((chat) => (
              <ListItem
                key={chat.id}
                onClick={() => onLoadChat(chat)}
                selected={selectedChat === chat.id}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    },
                  },
                }}
              >
                <ListItemText
                  primary={
                    editingTitle === chat.id ? (
                      <Box component="form" onSubmit={(e) => {
                        e.preventDefault();
                        onSaveTitle(chat.id);
                      }}>
                        <input
                          type="text"
                          value={editTitleValue}
                          onChange={(e) => onEditTitle(chat.id, e.target.value)}
                          onBlur={() => onCancelEdit()}
                          autoFocus
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#F9FAFB',
                            width: '100%',
                            fontSize: '0.875rem',
                            outline: 'none',
                            borderBottom: '1px solid rgba(255, 255, 255, 0.3)'
                          }}
                        />
                      </Box>
                    ) : (
                      chat.title
                    )
                  }
                  sx={{
                    '& .MuiListItemText-primary': {
                      color: '#F9FAFB',
                      fontSize: '0.875rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }
                  }}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditTitle(chat.id, chat.title);
                    }}
                    sx={{ color: '#F9FAFB', opacity: 0.7 }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={(e) => onDeleteChat(chat.id, e)}
                    sx={{ color: '#F9FAFB', opacity: 0.7 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Box>
      </Box>
    </SideDrawer>
  );
};

export default Sidebar;
